import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { fuseAnimations } from '@fuse/animations';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { TranslateService } from '@ngx-translate/core';
import { tap } from 'rxjs/operators';
import { ClientView } from '../../../../../../../../core/model/client-view.model';
import { TaxCodeDataService } from '../../../../../../../../core/service/tax-code.data.service';
import { taxCodeValidator } from '../../../../../../../../core/validator/tax-code.validator';
import { ClientService } from '../../client.service';
import { locale as english } from './i18n/en';
import { locale as italian } from './i18n/it';

@Component({
  selector: 'app-client-dialog',
  templateUrl: './client-dialog.component.html',
  styleUrls: ['./client-dialog.component.scss'],
  animations: fuseAnimations,
})
export class ClientDialogComponent implements OnInit {
  clientFormGroup: UntypedFormGroup;
  title: string;
  actionConfirm: string;

  errorSave = false;
  isSaving = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: NewCompanyDialogModel,
    public taxCode: TaxCodeDataService,
    private _clientService: ClientService,
    private _dialogRef: MatDialogRef<ClientDialogComponent>,
    private _formBuilder: UntypedFormBuilder,
    private _translateService: TranslateService,
    private _translationLoader: FuseTranslationLoaderService,
  ) {
    this._translationLoader.loadTranslations(italian, english);
  }

  ngOnInit(): void {
    if (this.data.action === 'add') {
      this.title = this._translateService.instant('NEW_CLIENT');
      this.actionConfirm = this._translateService.instant('BTN.ADD');
    } else if (this.data.action === 'edit') {
      this.title = this._translateService.instant('EDIT_CLIENT');
      this.actionConfirm = this._translateService.instant('BTN.EDIT');
    }

    this.clientFormGroup = this._formBuilder.group({
      // id
      id: [this.data.client ? this.data.client.id : null],

      // Debtor
      businessName: new UntypedFormControl(
        {
          value: this.data.client ? this.data.client.businessName : null,
          disabled: this.data.client,
        },
        Validators.required,
      ),
      taxCodePrefix: new UntypedFormControl(
        {
          value: this.data.client ? this.data.client.taxCodePrefix : 'IT',
          disabled: this.data.client,
        },
        Validators.required,
      ),
      taxCode: new UntypedFormControl(
        {
          value: this.data.client ? this.data.client.taxCode : null,
          disabled: this.data.client,
        },
        [Validators.required, taxCodeValidator],
      ),

      // Other Data
      isNew: new UntypedFormControl(this.data.client ? this.data.client.isNew || false : false),
      averageInvoiceAmount: new UntypedFormControl(
        this.data.client ? this.data.client.averageInvoiceAmount || null : null,
        Validators.required,
      ),
      yearlyBilledAmount: new UntypedFormControl(
        this.data.client ? this.data.client.yearlyBilledAmount || null : null,
        Validators.required,
      ),
      existingInvoiceTotalAmount: new UntypedFormControl(
        this.data.client ? this.data.client.existingInvoiceTotalAmount || null : null,
        Validators.required,
      ),
      paymentMethod: new UntypedFormControl(
        this.data.client ? this.data.client.paymentMethod || null : null,
        Validators.required,
      ),
      paymentTerms: new UntypedFormControl(
        this.data.client ? this.data.client.paymentTerms || null : null,
        Validators.required,
      ),
      averagePaymentDelay: new UntypedFormControl(
        this.data.client ? this.data.client.averagePaymentDelay || null : null,
        Validators.required,
      ),
    });

    this.clientFormGroup
      .get('taxCodePrefix')
      .valueChanges.pipe(
      tap(val => {
        this.clientFormGroup.get('taxCode').setValidators([Validators.required]);
        if (val === 'IT') {
          this.clientFormGroup
            .get('taxCode')
            .setValidators([Validators.required, taxCodeValidator]);
        }
        this.clientFormGroup.get('taxCode').updateValueAndValidity();
      }),
    )
      .subscribe();

    if (this.data.client && this.data.client.isNew) {
      this.isNewIsChanged(this.data.client.isNew);
    }

    this.clientFormGroup.get('isNew').valueChanges
      .pipe(
        tap(val => this.isNewIsChanged(val)),
      )
      .subscribe();
  }

  isNewIsChanged(val: boolean) {
    this.clientFormGroup.get('averageInvoiceAmount').setValidators(null);
    this.clientFormGroup.get('yearlyBilledAmount').setValidators(null);
    this.clientFormGroup.get('existingInvoiceTotalAmount').setValidators(null);
    this.clientFormGroup.get('paymentMethod').setValidators(null);
    this.clientFormGroup.get('paymentTerms').setValidators(null);
    this.clientFormGroup.get('averagePaymentDelay').setValidators(null);
    this.clientFormGroup.get('averagePaymentDelay').disable({ onlySelf: true });
    if (!val) {
      this.clientFormGroup.get('averageInvoiceAmount').setValidators([Validators.required]);
      this.clientFormGroup.get('yearlyBilledAmount').setValidators([Validators.required]);
      this.clientFormGroup.get('existingInvoiceTotalAmount').setValidators([Validators.required]);
      this.clientFormGroup.get('paymentMethod').setValidators([Validators.required]);
      this.clientFormGroup.get('paymentTerms').setValidators([Validators.required]);
      this.clientFormGroup.get('averagePaymentDelay').setValidators([Validators.required]);
      this.clientFormGroup.get('averagePaymentDelay').enable({ onlySelf: true });
    }
    this.clientFormGroup.get('averageInvoiceAmount').updateValueAndValidity({ onlySelf: true });
    this.clientFormGroup.get('yearlyBilledAmount').updateValueAndValidity({ onlySelf: true });
    this.clientFormGroup.get('existingInvoiceTotalAmount').updateValueAndValidity({ onlySelf: true });
    this.clientFormGroup.get('paymentMethod').updateValueAndValidity({ onlySelf: true });
    this.clientFormGroup.get('paymentTerms').updateValueAndValidity({ onlySelf: true });
    this.clientFormGroup.get('averagePaymentDelay').updateValueAndValidity({ onlySelf: true });
  }

  checkError(value: AbstractControl): boolean {
    return value.invalid && (value.dirty || value.touched);
  }

  close(): void {
    this._dialogRef.close(false);
  }

  submit(): void {
    if (this.errorSave) {
      this.errorSave = false;
    }
    this.isSaving = true;
    this.clientFormGroup.disable();

    if (this.data.action === 'add') {
      this._clientService.save(this.clientFormGroup.getRawValue())
        .subscribe(
          _ => {
            this._dialogRef.close(true);
          },
          err => {
            this.clientFormGroup.enable();
            if (err.status === 409) {
              this.clientFormGroup.get('taxCode').setErrors({ forbidden: true });
            } else {
              this.errorSave = true;
            }
            this.isSaving = false;
          },
        );
    } else if (this.data.action === 'edit') {
      this._clientService.update(this.clientFormGroup.getRawValue())
        .subscribe(
          () => {
            this._dialogRef.close(true);
          },
          () => {
            this.errorSave = true;
            this.clientFormGroup.enable();
            this.isSaving = false;
          },
        );
    }
  }
}

export interface NewCompanyDialogModel {
  action: string;
  client: ClientView;
}
